class PayPalSDKManagerClass {
  private isLoading = false
  private isLoaded = false
  private loadPromise: Promise<boolean> | null = null
  private currentOptions: PayPalSDKOptions | null = null

  /**
   * Load the PayPal SDK with the given options
   */
  async loadSDK(options: PayPalSDKOptions): Promise<boolean> {
    // If already loaded with same options, return true
    if (this.isLoaded && this.areOptionsEqual(options, this.currentOptions)) {
      return true
    }

    // If currently loading with same options, return the existing promise
    if (this.isLoading && this.loadPromise && this.areOptionsEqual(options, this.currentOptions)) {
      return this.loadPromise
    }

    // Remove existing script if options changed
    if (this.isLoaded && !this.areOptionsEqual(options, this.currentOptions)) {
      this.removeExistingScript()
    }

    this.isLoading = true
    this.currentOptions = options

    this.loadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script')

      // Build PayPal SDK URL with parameters
      const params = new URLSearchParams({
        'client-id': options.clientId,
        currency: options.currency,
        intent: options.intent,
        'enable-funding': 'paypal,venmo',
        'disable-funding': 'card',
        components: 'funding-eligibility', // adds isFundingEligible to window.paypal
        // debug: true
      })

      if (options.merchantId) {
        params.set('merchant-id', options.merchantId)
      }

      if (options.locale) {
        params.set('locale', options.locale)
      }

      script.src = `https://www.paypal.com/sdk/js?${params.toString()}`
      script.async = true

      script.onload = () => {
        this.isLoading = false
        this.isLoaded = true
        resolve(true)
      }

      script.onerror = () => {
        this.isLoading = false
        this.isLoaded = false
        this.loadPromise = null
        reject(new Error('Failed to load PayPal SDK'))
      }

      document.head.appendChild(script)
    })

    try {
      return await this.loadPromise
    } catch (error) {
      this.loadPromise = null
      throw error
    }
  }

  /**
   * Check funding eligibility for PayPal and Venmo
   */
  async checkFundingEligibility(): Promise<FundingEligibility> {
    if (!this.isLoaded || !window.paypal) {
      throw new Error('PayPal SDK not loaded')
    }

    try {
      // Cast to any to avoid TypeScript conflicts with PayPal SDK types
      const paypalSDK = window.paypal as any

      return {
        paypal: paypalSDK.isFundingEligible('paypal') ?? false,
        venmo: paypalSDK.isFundingEligible('venmo') ?? false,
      }
    } catch (error) {
      console.error('Error checking PayPal funding eligibility:', error)
      return {
        paypal: false,
        venmo: false,
      }
    }
  }

  async loadAndCheckEligibility(options: PayPalSDKOptions): Promise<FundingEligibility> {
    await this.loadSDK(options)
    return this.checkFundingEligibility()
  }

  private removeExistingScript(): void {
    const existingScript = document.querySelector('script[src*="paypal.com/sdk/js"]')

    if (existingScript) {
      existingScript.remove()
    }

    this.isLoaded = false
    this.currentOptions = null

    // Clear window.paypal to force reload
    if (window.paypal) {
      delete window.paypal
    }
  }

  private areOptionsEqual(
    options1: PayPalSDKOptions | null,
    options2: PayPalSDKOptions | null,
  ): boolean {
    if (!options1 || !options2) return false

    return (
      options1.clientId === options2.clientId &&
      options1.merchantId === options2.merchantId &&
      options1.currency === options2.currency &&
      options1.intent === options2.intent &&
      options1.locale === options2.locale
    )
  }

  /**
   * Reset the SDK manager state
   */
  reset(): void {
    this.removeExistingScript()
    this.isLoading = false
    this.loadPromise = null
  }
}

interface PayPalSDKOptions {
  clientId: string
  merchantId?: string
  currency: string
  intent: 'capture' | 'authorize'
  locale?: string
}

export interface FundingEligibility {
  paypal: boolean
  venmo: boolean
}

export const PayPalSDKManager = new PayPalSDKManagerClass()
